import { ReactElement, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  newsAPI,
  NewsPortalData,
  NewsSearchResponse,
  NewsArticleSummary,
  BookmarkItemType,
} from '../../services/api';
import {
  Newspaper,
  ChevronRight,
  Loader2,
  Search,
  X,
} from 'lucide-react';
import BookmarkButton from '../../components/BookmarkButton';
import BookmarkBatchProvider from '../../components/BookmarkBatchProvider';
import Pagination from '../../components/Pagination';

function articleUrl(article: NewsArticleSummary): string {
  return newsAPI.getArticleHtmlUrl(article.source, article.id);
}

function bookmarkType(source: string): BookmarkItemType {
  if (source === 'economic') return 'news_economic';
  if (source === 'events') return 'news_events';
  return 'news_earnings';
}

// --- Section image map ---
const SECTION_IMAGES: Record<string, string> = {
  markets: '/images/news-markets.jpg',
  'trading-sessions': '/images/news-trading-sessions.jpg',
  economic: '/images/news-economic.jpg',
  editorial: '/images/news-editorial.jpg',
  events: '/images/news-events.jpg',
  world: '/images/news-world.jpg',
};

// --- Lead Story ---
function LeadStory({ article }: { article: NewsArticleSummary }): ReactElement {
  return (
    <div className="group block relative">
      <div className="absolute top-3 right-3 z-10 bg-black/40 backdrop-blur-sm rounded-lg">
        <BookmarkButton
          itemType={bookmarkType(article.source)}
          itemId={String(article.id)}
          title={article.title}
          subtitle={article.symbol ?? undefined}
          url={articleUrl(article)}
          size="md"
          className="text-white hover:text-amber-300"
        />
      </div>
      <a href={articleUrl(article)} className="block">
        <div className="relative rounded-xl overflow-hidden mb-4">
          <img
            src="/images/news-hero.jpg"
            alt="Financial News"
            className="w-full h-48 sm:h-64 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
            <span className="text-[11px] font-medium uppercase tracking-wide text-gray-300">
              {article.section}
              {article.symbol && <> &middot; <span className="text-slate-300">{article.symbol}</span></>}
              {article.key_stat && <> &middot; <span className="text-emerald-400">{article.key_stat}</span></>}
            </span>
            <h2 className="text-lg sm:text-2xl font-bold text-white group-hover:text-gray-200 transition-colors leading-snug mt-1" style={{ fontFamily: 'Georgia, serif' }}>
              {article.title}
            </h2>
          </div>
        </div>
        {article.summary && (
          <p className="text-sm text-gray-500 leading-relaxed mb-2">
            {article.summary}
          </p>
        )}
        <span className="text-xs text-gray-400">{article.time_ago}</span>
      </a>
    </div>
  );
}

// --- Feed Row ---
function FeedRow({ article }: { article: NewsArticleSummary }): ReactElement {
  return (
    <div className="group relative flex items-start gap-3 p-3.5 rounded-lg bg-white border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all duration-150">
      <a href={articleUrl(article)} className="flex-1 min-w-0 block">
        <h3 className="text-sm font-semibold text-gray-800 group-hover:text-gray-900 transition-colors leading-snug line-clamp-2 mb-1.5">
          {article.title}
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-medium uppercase text-gray-400">{article.section}</span>
          {article.symbol && (
            <>
              <span className="text-gray-200">&middot;</span>
              <span className="text-[10px] font-mono font-medium text-slate-500">{article.symbol}</span>
            </>
          )}
          {article.key_stat && (
            <>
              <span className="text-gray-200">&middot;</span>
              <span className="text-[10px] font-mono font-medium text-emerald-600">{article.key_stat}</span>
            </>
          )}
          <span className="text-gray-200">&middot;</span>
          <span className="text-[10px] text-gray-400">{article.time_ago}</span>
        </div>
      </a>
      <BookmarkButton
        itemType={bookmarkType(article.source)}
        itemId={String(article.id)}
        title={article.title}
        subtitle={article.symbol ?? undefined}
        url={articleUrl(article)}
        size="sm"
      />
    </div>
  );
}

// --- Section Row (compact, for bottom columns) ---
function SectionRow({ article }: { article: NewsArticleSummary }): ReactElement {
  return (
    <div className="group flex items-start gap-2.5 py-2.5">
      <a href={articleUrl(article)} className="flex-1 min-w-0 block">
        <h3 className="text-[13px] font-medium text-gray-700 group-hover:text-gray-900 transition-colors leading-snug line-clamp-2">
          {article.title}
        </h3>
        <div className="flex items-center gap-1.5 mt-1 text-[10px] text-gray-400">
          <span className="font-medium uppercase">{article.section}</span>
          {article.symbol && (
            <>
              <span>&middot;</span>
              <span className="font-mono text-slate-500">{article.symbol}</span>
            </>
          )}
          <span>&middot;</span>
          <span>{article.time_ago}</span>
        </div>
      </a>
      <BookmarkButton
        itemType={bookmarkType(article.source)}
        itemId={String(article.id)}
        title={article.title}
        subtitle={article.symbol ?? undefined}
        url={articleUrl(article)}
        size="sm"
      />
    </div>
  );
}

// --- Section Block ---
function SectionBlock({ sectionKey, data }: { sectionKey: string; data: { name: string; articles: NewsArticleSummary[]; total_count: number } }): ReactElement {
  if (data.articles.length === 0) return <></>;

  const imageUrl = SECTION_IMAGES[sectionKey];

  return (
    <div>
      {/* Section image header */}
      {imageUrl && (
        <div className="relative rounded-lg overflow-hidden mb-3">
          <img
            src={imageUrl}
            alt={data.name}
            className="w-full h-28 sm:h-36 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 px-3 pb-2.5">
            <h2 className="text-xs font-bold text-white uppercase tracking-wide">
              {data.name}
            </h2>
          </div>
        </div>
      )}
      {!imageUrl && (
        <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200">
          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
            {data.name}
          </h2>
        </div>
      )}
      {data.total_count > data.articles.length && (
        <div className="flex justify-end mb-2">
          <Link
            to={`/news/section/${sectionKey}`}
            className="flex items-center gap-0.5 text-xs text-blue-600 hover:text-blue-500 transition-colors"
          >
            More
            <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}
      <div className="divide-y divide-gray-50">
        {data.articles.map((article) => (
          <SectionRow key={`${article.source}-${article.id}`} article={article} />
        ))}
      </div>
    </div>
  );
}

// --- Main Portal ---
export default function NewsPortal(): ReactElement {
  const [searchInput, setSearchInput] = useState('');
  const [activeSymbol, setActiveSymbol] = useState<string | undefined>();
  const [searchPage, setSearchPage] = useState(0);
  const searchLimit = 20;

  const isSearchMode = !!activeSymbol;

  // Portal query (normal mode)
  const { data: portalData, isLoading: portalLoading, error: portalError } = useQuery<NewsPortalData>({
    queryKey: ['news-portal'],
    queryFn: async () => {
      const response = await newsAPI.getPortalData(15);
      return response.data;
    },
    staleTime: 0,
    enabled: !isSearchMode,
  });

  // Search query (symbol mode)
  const { data: searchData, isLoading: searchLoading, error: searchError } = useQuery<NewsSearchResponse>({
    queryKey: ['news-search', activeSymbol, searchPage],
    queryFn: async () => {
      const response = await newsAPI.searchBySymbol(activeSymbol!, {
        limit: searchLimit,
        offset: searchPage * searchLimit,
      });
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
    enabled: isSearchMode,
  });

  const isLoading = isSearchMode ? searchLoading : portalLoading;
  const error = isSearchMode ? searchError : portalError;

  const handleSearch = () => {
    const val = searchInput.trim().toUpperCase();
    if (val) {
      setSearchPage(0);
      setActiveSymbol(val);
    }
  };

  const clearSearch = () => {
    setSearchInput('');
    setActiveSymbol(undefined);
    setSearchPage(0);
  };

  const SECTION_ORDER = ['markets', 'trading-sessions', 'economic', 'editorial', 'events', 'world'];
  const sectionKeys = portalData
    ? SECTION_ORDER.filter((k) => portalData.sections[k]?.articles?.length)
    : [];

  // Collect every article on the page so the batch endpoint checks them all in
  // one round-trip instead of one /check per card.
  const batchItems = (() => {
    if (!portalData && !searchData) return [];
    if (isSearchMode && searchData) {
      return searchData.articles.map((a) => ({
        itemType: bookmarkType(a.source),
        itemId: String(a.id),
      }));
    }
    if (!portalData) return [];
    return [
      ...(portalData.lead ? [{ itemType: bookmarkType(portalData.lead.source), itemId: String(portalData.lead.id) }] : []),
      ...portalData.feed.map((a) => ({
        itemType: bookmarkType(a.source),
        itemId: String(a.id),
      })),
      ...sectionKeys.flatMap((k) =>
        portalData.sections[k].articles.map((a) => ({
          itemType: bookmarkType(a.source),
          itemId: String(a.id),
        }))
      ),
    ];
  })();

  return (
    <BookmarkBatchProvider items={batchItems}>
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">

        {/* Symbol Search */}
        <div className="mb-6">
          <form
            onSubmit={(e) => { e.preventDefault(); handleSearch(); }}
            className="flex items-center gap-2 max-w-sm"
          >
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value.toUpperCase())}
                placeholder="Search by symbol..."
                className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 font-mono placeholder:font-sans"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors"
            >
              Search
            </button>
            {isSearchMode && (
              <button
                type="button"
                onClick={clearSearch}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </form>
          {isSearchMode && searchData && (
            <p className="text-xs text-gray-500 mt-2">
              {searchData.total_count} article{searchData.total_count !== 1 ? 's' : ''} found for <span className="font-mono font-semibold text-gray-700">{activeSymbol}</span>
            </p>
          )}
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400 mb-3" />
            <p className="text-sm text-gray-500">Loading news...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
              <Newspaper className="w-8 h-8 text-red-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-700 mb-2">Failed to load news</h3>
            <p className="text-sm text-gray-500">Please try again later</p>
          </div>
        )}

        {/* Search Results */}
        {isSearchMode && searchData && !isLoading && (
          <>
            {searchData.articles.length > 0 ? (
              <div className="space-y-2">
                {searchData.articles.map((article) => (
                  <FeedRow key={`${article.source}-${article.id}`} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                  <Search className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">No articles found</h3>
                <p className="text-sm text-gray-500">No news articles match <span className="font-mono font-semibold">{activeSymbol}</span></p>
              </div>
            )}

            {/* Pagination */}
            <Pagination
              page={searchPage}
              totalPages={searchData.total_pages}
              onPageChange={setSearchPage}
              hasMore={searchData.has_more}
            />
          </>
        )}

        {/* Portal Content (normal mode) */}
        {!isSearchMode && portalData && !isLoading && (
          <>
            {/* Top: Lead (left) + Latest feed (right) */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-5 mb-6 sm:mb-8">
              {/* Lead story — spans 3 of 5 columns */}
              <div className="lg:col-span-3">
                {portalData.lead && <LeadStory article={portalData.lead} />}
                {/* Sub-stories below lead */}
                {portalData.feed.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-4">
                    {portalData.feed.slice(0, 6).map((article) => (
                      <FeedRow key={`sub-${article.source}-${article.id}`} article={article} />
                    ))}
                  </div>
                )}
              </div>

              {/* Latest feed sidebar — spans 2 of 5 columns */}
              <div className="lg:col-span-2 space-y-2">
                {portalData.feed.slice(6, 12).map((article) => (
                  <FeedRow key={`side-${article.source}-${article.id}`} article={article} />
                ))}
              </div>
            </div>

            {/* More stories (two-column, even count) */}
            {portalData.feed.length > 12 && (() => {
              const remaining = portalData.feed.slice(12);
              const evenItems = remaining.slice(0, remaining.length % 2 === 0 ? remaining.length : remaining.length - 1);
              return (
                <div className="mb-10">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                    {evenItems.map((article) => (
                      <FeedRow key={`${article.source}-${article.id}`} article={article} />
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Section Lists (2-column) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 pt-5 sm:pt-6 border-t border-gray-200">
              {sectionKeys.map((sectionKey) => {
                const sectionData = portalData.sections[sectionKey];
                if (!sectionData || sectionData.articles.length === 0) return null;
                return (
                  <SectionBlock
                    key={sectionKey}
                    sectionKey={sectionKey}
                    data={sectionData}
                  />
                );
              })}
            </div>

            {/* Empty State */}
            {portalData.total_articles === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-200 flex items-center justify-center">
                  <Newspaper className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">No news yet</h3>
                <p className="text-sm text-gray-500">Check back later</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
    </BookmarkBatchProvider>
  );
}
